import { BlockStatus, type LiveTimeBlock } from '@wiztime/core';
import type { TFunction } from 'i18next';
import type { TipLine } from './FloatTip';
import type { DisplayGroup } from './laneModel';
import { PreviewKind, type LanePreview } from './useLaneInteractions';

export interface TipLineInput {
  readonly preview: LanePreview;
  /**
   * Overlap candidates: the visible blocks of the day the preview lies on — the source day
   * normally, the target day's blocks during a cross-day move (§2.8, overlap hint §2.1.3).
   */
  readonly blocks: readonly LiveTimeBlock[];
  /** The group being dragged; undefined while creating. */
  readonly draggedGroup: DisplayGroup | undefined;
  /** Minimum duration of a pointer gesture (2026-10b §3.3): the resize floor the tip names. */
  readonly minDurationMin: number;
  /** Project name of a block, for the overlap line. */
  readonly projectName: (projectId: string) => string;
  readonly t: TFunction;
}

/**
 * Second line of the floating tip, by the §3.4.3 priority: delete warning > draft held back
 * from crossing days (§2.8) > draft confirmation > overlap > flip > minimum duration. Null
 * when none applies (the tip then shows only the time range line).
 */
export function dragTipLine2(input: TipLineInput): TipLine | null {
  const { preview, blocks, draggedGroup, minDurationMin, projectName, t } = input;
  // A draft dragged past another lane's centre line stays on its day (§2.8).
  if (preview.crossDenied) return { text: t('tip.draftCrossDay') };
  const deleteIds = preview.kind === PreviewKind.Resize ? (preview.group?.deleteIds ?? []) : [];
  if (deleteIds.length > 0) {
    const noted = blocks.filter(
      (block) => deleteIds.includes(block.id) && (block.data.note ?? '') !== '',
    ).length;
    return {
      text:
        noted > 0
          ? t('tip.deleteMembersNotes', { count: deleteIds.length, notes: noted })
          : t('tip.deleteMembers', { count: deleteIds.length }),
      warn: noted > 0,
    };
  }
  if (preview.kind !== PreviewKind.Create && draggedGroup?.status === BlockStatus.Draft) {
    return { text: t('tip.confirmDraft') };
  }
  const excluded = new Set(draggedGroup?.memberIds ?? []);
  let best: { name: string; minutes: number } | null = null;
  for (const block of blocks) {
    if (excluded.has(block.id)) continue;
    const minutes =
      Math.min(preview.span.endMin, block.data.endMin) - Math.max(preview.span.startMin, block.data.startMin);
    if (minutes <= 0 || (best !== null && minutes <= best.minutes)) continue;
    best = { name: projectName(block.data.projectId), minutes };
  }
  if (best !== null) return { text: t('tip.overlapWith', { project: best.name, minutes: best.minutes }) };
  if (preview.flipped) return { text: t('tip.flipped') };
  if (preview.kind === PreviewKind.Resize && preview.span.endMin - preview.span.startMin === minDurationMin) {
    return { text: t('tip.minDuration', { minutes: minDurationMin }) };
  }
  return null;
}
